'use client';

import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { ArrowRight, Merge, X } from 'lucide-react';
import { Button } from '@/components/ui/Button/Button';
import LoadingDots from '@/components/ui/LoadingDots/LoadingDots';
import ProfileAvatar from '@/components/ui/ProfileUser/ProfileAvatar';
import { toast } from '@/components/ui/Toasts/use-toast';
import {
  AccountImpactSummary,
  EmailGateFields,
  EscalationFields,
  PreviewLoading,
  emailGateReady,
  emptyEmailGate,
  useAccountPreview,
  type EmailGateState,
} from './AccountGates';

/**
 * Merge one account into another. Everything the source owns moves to the
 * target, then the source is deleted and its username is freed — there is no
 * redirect from the old profile URL.
 *
 * The target picker is the same debounced /api/admin/users/search flow as
 * ChangeOwnershipModal.
 */

interface User {
  id: string;
  username: string;
  avatar_url: string;
}

interface Props {
  trigger: React.ReactNode;
  sourceId: string;
  sourceUsername: string | null;
  /** Where to go once the source no longer exists. */
  redirectTo?: string;
  onSuccess?: () => void;
}

export const MergeAccountModal = ({
  trigger,
  sourceId,
  sourceUsername,
  redirectTo,
  onSuccess,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const router = useRouter();
  const modalRef = useRef<HTMLDivElement>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [emailGate, setEmailGate] = useState<EmailGateState>(emptyEmailGate);
  const [acknowledged, setAcknowledged] = useState(false);
  const [typedUsername, setTypedUsername] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { preview: sourcePreview, loading, error: previewError } = useAccountPreview(
    isOpen ? sourceId : null
  );
  const { preview: targetPreview } = useAccountPreview(selectedUser?.id ?? null);

  const reset = () => {
    setSearchQuery('');
    setUsers([]);
    setSelectedUser(null);
    setEmailGate(emptyEmailGate);
    setAcknowledged(false);
    setTypedUsername('');
    setError(null);
  };

  const toggleModal = () => {
    if (isSubmitting) return;
    if (isOpen) reset();
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        toggleModal();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isSubmitting]);

  useEffect(() => {
    if (searchQuery.trim().length < 1) {
      setUsers([]);
      return;
    }
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const response = await fetch(
          `/api/admin/users/search?query=${encodeURIComponent(searchQuery.trim())}`
        );
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.error || 'Failed to search users');
        }
        // The source can't be its own merge target.
        setUsers((data.users || []).filter((u: User) => u.id !== sourceId));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to search users');
        setUsers([]);
      } finally {
        setIsSearching(false);
        searchTimeoutRef.current = null;
      }
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, sourceId]);

  const escalating = sourcePreview?.requiresEscalation ?? false;
  const canSubmit =
    !!sourcePreview &&
    !!selectedUser &&
    !isSubmitting &&
    emailGateReady(emailGate) &&
    (!escalating || (acknowledged && typedUsername.trim() === (sourcePreview.username ?? '')));

  const handleSubmit = async () => {
    if (!sourcePreview || !selectedUser) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const response = await fetch('/api/admin/users/merge', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sourceId,
          targetId: selectedUser.id,
          email: emailGate.skip ? undefined : emailGate.email.trim(),
          skipEmailVerification: emailGate.skip,
          skipReason: emailGate.skip ? emailGate.skipReason.trim() : undefined,
          escalationAck: escalating ? acknowledged : undefined,
          confirmSourceUsername: escalating ? typedUsername.trim() : undefined,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to merge accounts');
      }

      toast({
        title: `Merged into @${data.targetUsername ?? selectedUser.username}`,
        // search_tones and tones_aggs refresh on pg_cron every ~5 minutes, so
        // the target's profile and search results lag. Saying so up front
        // stops that reading as a failed merge.
        description:
          'Content moved and the old username was retired. Search and profile counts catch up within about 5 minutes.',
      });

      reset();
      setIsOpen(false);
      onSuccess?.();
      if (redirectTo) {
        router.push(redirectTo);
      } else {
        router.refresh();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to merge accounts');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <button onClick={toggleModal} className="text-zinc-500 cursor-pointer hover:text-white p-1">
        {trigger}
      </button>
      {isOpen && (
        <span className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-40">
          <span
            ref={modalRef}
            className="bg-zinc-900 border border-zinc-500 border-opacity-50 text-white w-full max-w-lg rounded-xl shadow-xl flex flex-col max-h-[90vh] overflow-hidden"
          >
            <span className="flex justify-between items-center p-4 border-b border-zinc-700 flex-shrink-0">
              <span className="text-xl font-semibold flex items-center gap-2">
                <Merge className="w-5 h-5" />
                Merge account
              </span>
              <button onClick={toggleModal} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </span>

            <span className="p-4 flex flex-col gap-4 overflow-y-auto">
              {loading && <PreviewLoading />}
              {previewError && <div className="text-red-400 text-sm">{previewError}</div>}

              {sourcePreview && (
                <>
                  <AccountImpactSummary preview={sourcePreview} heading="Moving from" />

                  <div className="flex flex-col gap-2">
                    <label className="text-zinc-400 text-sm">Merge into</label>
                    <div className="w-full relative flex">
                      <input
                        type="text"
                        placeholder="Search"
                        className="w-full bg-zinc-900 outline-none rounded-full border border-zinc-700 pl-4 pr-10 min-h-[38px] flex-1"
                        value={searchQuery}
                        disabled={isSubmitting}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                      {searchQuery.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setSearchQuery('')}
                          className="absolute right-4 top-1/2 transform -translate-y-1/2 text-zinc-400 hover:text-zinc-300"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      )}
                    </div>

                    {searchQuery.trim().length >= 1 && (
                      <div className="flex flex-col gap-1 max-h-48 overflow-y-auto">
                        {isSearching ? (
                          <div className="flex items-center gap-2 py-4 text-zinc-400">
                            <LoadingDots />
                            <span>Searching...</span>
                          </div>
                        ) : users.length > 0 ? (
                          users.map((u) => (
                            <button
                              key={u.id}
                              type="button"
                              onClick={() => setSelectedUser(u)}
                              className={`w-full px-3 py-2 text-left flex items-center gap-2 transition-colors rounded-lg ${
                                selectedUser?.id === u.id ? 'bg-zinc-700' : 'hover:bg-zinc-800'
                              }`}
                              style={selectedUser?.id === u.id ? { color: 'white' } : {}}
                            >
                              <ProfileAvatar
                                username={u.username}
                                avatarUrl={u.avatar_url}
                                ownProfile={false}
                                size={24}
                              />
                              {u.username}
                            </button>
                          ))
                        ) : (
                          <div className="py-4 text-zinc-500 text-sm">No users found</div>
                        )}
                      </div>
                    )}
                  </div>

                  {targetPreview && (
                    <>
                      <div className="flex items-center justify-center gap-2 text-zinc-500 text-sm">
                        <span>@{sourcePreview.username}</span>
                        <ArrowRight className="h-4 w-4" />
                        <span className="text-white">@{targetPreview.username}</span>
                      </div>
                      <AccountImpactSummary preview={targetPreview} heading="Merging into" />
                    </>
                  )}

                  <span className="text-zinc-400 text-sm">
                    Tones, models, trainings, comments and API keys move to the target. Duplicate
                    favorites, votes, mentions and follows are dropped rather than duplicated.
                    The source account is then deleted and{' '}
                    <span className="text-white">@{sourcePreview.username}</span> becomes available
                    again — its profile URL will 404.
                  </span>

                  <EmailGateFields
                    state={emailGate}
                    onChange={setEmailGate}
                    subject="the source account"
                    disabled={isSubmitting}
                  />

                  {escalating && (
                    <EscalationFields
                      preview={sourcePreview}
                      typedUsername={typedUsername}
                      onTypedUsernameChange={setTypedUsername}
                      acknowledged={acknowledged}
                      onAcknowledgedChange={setAcknowledged}
                      disabled={isSubmitting}
                      consequence="The content survives under the target, but retiring the username does not come back."
                    />
                  )}

                  {error && <div className="text-red-400 text-sm">{error}</div>}

                  <span className="flex flex-row gap-4 pt-2">
                    <Button
                      className="w-full"
                      onClick={toggleModal}
                      isSecondary={true}
                      variant="slim"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="w-full"
                      onClick={handleSubmit}
                      isPrimary={true}
                      variant="slim"
                      disabled={!canSubmit}
                    >
                      {isSubmitting ? <LoadingDots /> : 'Merge and retire'}
                    </Button>
                  </span>
                </>
              )}
            </span>
          </span>
        </span>
      )}
    </>
  );
};

export default MergeAccountModal;
